/**
 * Helpers for the create-challenge games step (filtering, kickoff time).
 */
import { getTimeOnlyFromFormatted } from '../../../helpers/formatDateTime.helper';
import type { Game, PickSlip } from '../types/create.types';

/**
 * Parse kickoff from the same date + time we show in the UI (game.date + game.time).
 * So "not started" is compared against what the user sees, not raw API/UTC.
 */
export function parseKickoffFromDisplay(game: Game): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(game.date ?? '').trim());
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const timePart = getTimeOnlyFromFormatted(game.time ?? '');
  const [hh = 12, mm = 0] = timePart ? timePart.split(':').map(Number) : [12, 0];
  const kickoff = new Date(y, m - 1, d, hh, mm);
  return Number.isNaN(kickoff.getTime()) ? null : kickoff;
}

/**
 * Earliest kickoff among games referenced by pickSlips. Used for "Start in" countdown (e.g. SuccessModal).
 */
export function getEarliestKickoffFromPickSlips(pickSlips: PickSlip[], games: Game[]): Date | null {
  let earliest: Date | null = null;
  for (const slip of pickSlips) {
    const game = games.find((g) => g.id === slip.gameId);
    if (!game) continue;
    const kickoff = parseKickoffFromDisplay(game);
    if (kickoff && (!earliest || kickoff.getTime() < earliest.getTime())) {
      earliest = kickoff;
    }
  }
  return earliest;
}

/**
 * Returns true if the game's kickoff time is in the future (game has not started yet).
 * Uses the same date + time shown in the UI so the filter matches what the user sees.
 */
export function isGameNotStarted(game: Game): boolean {
  const kickoff = parseKickoffFromDisplay(game);
  return kickoff != null && kickoff.getTime() > Date.now();
}
